import { Box, LinearProgress, Link, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

const CONTAINER_NAME = 'entitlement-card';

/**
 * Container wrapper with container query support
 */
export const CardContainer = styled(Box)(({ theme }) => ({
  containerType: 'inline-size',
  containerName: CONTAINER_NAME,
  width: '100%',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[50], // #FAF9FC (closest to designer's #FAFAFA)
  borderRadius: 10,
  border: `1px solid ${theme.palette.grey[200]}`, // #EBEAF1 (closest to designer's #EBEBEB)
  boxSizing: 'border-box',

  // Optional: disable border radius
  '&[data-no-border-radius="true"]': {
    borderRadius: 0,
  },
}));

/**
 * Main content wrapper
 */
export const ContentWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  width: '100%',

  // Compact: < 350px - very tight spacing
  [`@container ${CONTAINER_NAME} (max-width: 349px)`]: {
    gap: theme.spacing(1),
  },

  // Standard: 350px - 600px - normal spacing
  [`@container ${CONTAINER_NAME} (min-width: 350px)`]: {
    gap: theme.spacing(1.5),
  },

  // Wide: 601px - 800px - more generous spacing
  [`@container ${CONTAINER_NAME} (min-width: 601px)`]: {
    gap: theme.spacing(2),
  },

  // Extra Wide: > 800px - side-by-side layout
  [`@container ${CONTAINER_NAME} (min-width: 801px)`]: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(6),
  },
}));

/**
 * Left side wrapper (TopRow + ProgressBar)
 */
export const LeftSide = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  width: '100%',

  // Extra Wide: flex to take available space
  [`@container ${CONTAINER_NAME} (min-width: 801px)`]: {
    flex: 1,
  },
}));

/**
 * Top row: usage text and remaining text
 */
export const TopRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: theme.spacing(1),
  flexWrap: 'wrap',

  // Compact: stack vertically
  [`@container ${CONTAINER_NAME} (max-width: 349px)`]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
  },
}));

/**
 * Usage text (left side)
 */
export const UsageText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  lineHeight: '1.2',
  fontWeight: 500,
  color: theme.palette.grey[800], // #2A2533 (closest to designer's #2A2A2A)

  // Compact: slightly smaller
  [`@container ${CONTAINER_NAME} (max-width: 349px)`]: {
    fontSize: '0.8125rem',
  },

  // Wide: slightly larger
  [`@container ${CONTAINER_NAME} (min-width: 601px)`]: {
    fontSize: '0.9375rem',
  },
}));

/**
 * Remaining text (right side)
 */
export const RemainingText = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  lineHeight: '1.2',
  fontWeight: 400,
  color: theme.palette.grey[600], // #857F93 (closest to designer's #787878)

  // Compact: smaller
  [`@container ${CONTAINER_NAME} (max-width: 349px)`]: {
    fontSize: '0.75rem',
  },

  // Wide: slightly larger
  [`@container ${CONTAINER_NAME} (min-width: 601px)`]: {
    fontSize: '0.875rem',
  },
}));

/**
 * Depletion message (warning text)
 */
export const DepletionText = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  fontWeight: 400,
  color: theme.palette.grey[600], // #857F93 (closest to designer's #787878)
  fontStyle: 'italic',
  lineHeight: '1.2',

  // Compact: smaller
  [`@container ${CONTAINER_NAME} (max-width: 349px)`]: {
    fontSize: '0.75rem',
  },
}));

/**
 * After trial text (subtitle for trial mode)
 */
export const AfterTrialText = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  lineHeight: '1.2',
  fontWeight: 400,
  color: theme.palette.grey[600], // #857F93 (closest to designer's #787878)

  // Style the "After trial:" prefix (strong tag)
  '& strong': {
    color: theme.palette.grey[800], // #2A2533 - darker for emphasis
  },

  // Compact: smaller
  [`@container ${CONTAINER_NAME} (max-width: 349px)`]: {
    fontSize: '0.75rem',
  },

  // Wide: slightly larger
  [`@container ${CONTAINER_NAME} (min-width: 601px)`]: {
    fontSize: '0.875rem',
  },
}));

/**
 * Progress bar wrapper
 */
export const ProgressBarWrapper = styled(Box)(() => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
}));

/**
 * Styled progress bar (always primary purple color)
 */
export const StyledProgressBar = styled(LinearProgress)(({ theme }) => ({
  width: '100%',
  height: 8,
  borderRadius: 4,
  backgroundColor: theme.palette.grey[300], // #D5D3E0 (closest to designer's #E4E4E4)

  '& .MuiLinearProgress-bar': {
    backgroundColor: theme.palette.primary.main, // Always primary purple
    borderRadius: 4,
  },

  // Compact: slightly thinner
  [`@container ${CONTAINER_NAME} (max-width: 349px)`]: {
    height: 6,
  },

  // Wide: slightly thicker
  [`@container ${CONTAINER_NAME} (min-width: 601px)`]: {
    height: 10,
  },
}));

/**
 * Bottom row: reset date and upgrade link
 */
export const BottomRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexWrap: 'wrap',

  // Compact: stack vertically
  [`@container ${CONTAINER_NAME} (max-width: 349px)`]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
  },

  // Wide: 801px - 1000px - vertical stack on right side
  [`@container ${CONTAINER_NAME} (min-width: 801px)`]: {
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: theme.spacing(0.75),
    minWidth: '200px',
  },

  // Extra Wide: > 1000px - horizontal on right side, right-aligned
  [`@container ${CONTAINER_NAME} (min-width: 1001px)`]: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(4),
    minWidth: '250px',
  },
}));

/**
 * Reset date text (left side)
 */
export const ResetDateText = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 400,
  color: theme.palette.grey[600], // #857F93 (closest to designer's #787878)
  lineHeight: '1.2',

  // Compact: smaller
  [`@container ${CONTAINER_NAME} (max-width: 349px)`]: {
    fontSize: '0.6875rem',
  },

  // Wide: slightly larger
  [`@container ${CONTAINER_NAME} (min-width: 601px)`]: {
    fontSize: '0.8125rem',
  },
}));

/**
 * Upgrade link (right side)
 */
export const UpgradeLink = styled(Link)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 500,
  color: theme.palette.grey[700], // #4D4758 (closest to designer's #585858)
  textDecoration: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  lineHeight: '1.2',

  '&:hover': {
    color: theme.palette.grey[800], // Darker on hover
    textDecoration: 'underline',
  },

  // Compact: smaller
  [`@container ${CONTAINER_NAME} (max-width: 349px)`]: {
    fontSize: '0.6875rem',
  },

  // Wide: slightly larger
  [`@container ${CONTAINER_NAME} (min-width: 601px)`]: {
    fontSize: '0.8125rem',
  },
}));

/**
 * Loading skeleton wrapper
 */
export const SkeletonWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  width: '100%',
}));

/**
 * Trial content wrapper - CSS Grid layout
 * Narrow: 1 column, 3 rows (Title, Progress, AfterTrialText)
 * Wide (>800px): 2 columns, 2 rows (Title left, AfterTrialText right, Progress spans full width)
 */
export const TrialContentWrapper = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gridTemplateRows: 'auto auto auto',
  gap: theme.spacing(1.5),
  width: '100%',

  // Extra Wide: > 800px - 2 columns, title left, after-trial right, progress spans full width
  [`@container ${CONTAINER_NAME} (min-width: 801px)`]: {
    gridTemplateColumns: '1fr auto',
    gridTemplateRows: 'auto auto',
  },

  // Grid placement for child elements in trial mode using nth-child
  '& > *:nth-child(1)': {
    // UsageText (Title) - row 1, col 1
    gridColumn: '1',
    gridRow: '1',
    [`@container ${CONTAINER_NAME} (min-width: 801px)`]: {
      gridColumn: '1',
      gridRow: '1',
    },
  },

  '& > *:nth-child(2)': {
    // ProgressBarWrapper - row 2, col 1 (spans all cols in wide)
    gridColumn: '1',
    gridRow: '2',
    [`@container ${CONTAINER_NAME} (min-width: 801px)`]: {
      gridColumn: '1 / -1', // Span all columns
      gridRow: '2',
    },
  },

  '& > *:nth-child(3)': {
    // AfterTrialText - row 3, col 1 (moves to row 1 col 2 in wide)
    gridColumn: '1',
    gridRow: '3',
    [`@container ${CONTAINER_NAME} (min-width: 801px)`]: {
      gridColumn: '2',
      gridRow: '1',
    },
  },
}));
